import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Feature,
    LicenseRaw,
    Site,
    User,
} from '@myrtls/api-interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    FEATURES_ENDPOINT,
    LICENSE_ENDPOINT,
    SITES_API_URL,
    SNACK_BAR_DURATION,
    USERS_ENDPOINT,
} from '../../shared/utils/constants';
import { AppState } from '../index';
import { showSnackBarAction } from '../snack-bar/snack-bar.actions';
import {
    deleteUserFailureAction,
    deleteUserRequestAction,
    deleteUserSuccessAction,
    editUserPermissionsFailureAction,
    editUserPermissionsRequestAction,
    editUserPermissionsSuccessAction,
    getSiteFailureAction,
    getSiteFeaturesFailureAction,
    getSiteFeaturesRequestAction,
    getSiteFeaturesSuccessAction,
    getSiteLicenseRawFailureAction,
    getSiteLicenseRawRequestAction,
    getSiteLicenseRawSuccessAction,
    getSiteRequestAction,
    getSitesFailureAction,
    getSitesRequestAction,
    getSitesSuccessAction,
    getSiteSuccessAction,
    getSiteUsersFailureAction,
    getSiteUsersRequestAction,
    getSiteUsersSuccessAction,
    inviteUserFailureAction,
    inviteUserRequestAction,
    inviteUserSuccessAction,
} from './sites.actions';
import { $selectedSiteId } from './sites.selectors';
import { ActivatedRoute, ActivationStart, Router } from '@angular/router';
import { SnackBarType } from '../../shared/models/snack-bar-config.model';
import { of } from 'rxjs';

@Injectable()
export class SitesEffects {
    private httpWithoutAuth: HttpClient;

    getSites$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(getSitesRequestAction),
                withLatestFrom(
                    this.router.events.pipe(
                        filter(
                            (event): event is ActivationStart =>
                                event instanceof ActivationStart,
                        ),
                    ),
                ),
            )
            .pipe(
                switchMap(([, event]) => {
                    const params = event.snapshot.queryParams;
                    let httpSites;

                    if (params.serverToken) {
                        httpSites = this.httpWithoutAuth.get<Site[]>(
                            SITES_API_URL,
                            {
                                headers: {
                                    Authorization:
                                        'Bearer ' + params.serverToken,
                                },
                            },
                        );
                    } else {
                        httpSites = this.http.get<Site[]>(SITES_API_URL);
                    }

                    return httpSites.pipe(
                        map(sites => {
                            return getSitesSuccessAction({ sites });
                        }),
                        catchError(error =>
                            of(getSitesFailureAction({ error })),
                        ),
                    );
                }),
            ),
    );

    getSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((siteId): siteId is string => siteId != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http.get<Site>(`${SITES_API_URL}/${siteId}`).pipe(
                    map(project => getSiteSuccessAction({ site: project })),
                    catchError(error => of(getSiteFailureAction({ error }))),
                ),
            ),
        ),
    );

    getSiteUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteUsersRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<User[]>(`${SITES_API_URL}/${siteId}/${USERS_ENDPOINT}`)
                    .pipe(
                        map(users =>
                            getSiteUsersSuccessAction({ siteId, users }),
                        ),
                        catchError(error =>
                            of(getSiteUsersFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    inviteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(inviteUserRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ user }, siteId]) =>
                this.http
                    .post<string>(
                        `${SITES_API_URL}/${siteId}/${USERS_ENDPOINT}`,
                        user,
                    )
                    .pipe(
                        mergeMap(id => [
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    duration: SNACK_BAR_DURATION,
                                    description:
                                        'User was invited successfully!',
                                },
                            }),
                            inviteUserSuccessAction({
                                siteId,
                                user: { ...user, id },
                            }),
                            getSiteUsersRequestAction(),
                        ]),
                        catchError(error =>
                            of(inviteUserFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    editUserPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(editUserPermissionsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ user }, siteId]) =>
                this.http
                    .put<void>(
                        `${SITES_API_URL}/${siteId}/${USERS_ENDPOINT}/${user.id}`,
                        {
                            name: user.name,
                            permissions: user.permissions,
                        },
                    )
                    .pipe(
                        mergeMap(() => [
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    duration: SNACK_BAR_DURATION,
                                    description:
                                        "User's permissions has changed successfully!",
                                },
                            }),
                            editUserPermissionsSuccessAction({
                                siteId,
                                user,
                            }),
                            getSiteUsersRequestAction(),
                        ]),
                        catchError(error =>
                            of(editUserPermissionsFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    deleteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteUserRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ userId }, siteId]) =>
                this.http
                    .delete<void>(
                        `${SITES_API_URL}/${siteId}/${USERS_ENDPOINT}/${userId}`,
                    )
                    .pipe(
                        mergeMap(() => [
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    duration: SNACK_BAR_DURATION,
                                    description:
                                        'User was removed successfully!',
                                },
                            }),
                            deleteUserSuccessAction({ siteId, userId }),
                        ]),
                        catchError(error =>
                            of(deleteUserFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    getSiteFeatures$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteFeaturesRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
                this.route.queryParams,
            ),
            switchMap(([, siteId, params]) => {
                if (params.serverToken) {
                    return this.httpWithoutAuth
                        .get<Feature[]>(
                            `${SITES_API_URL}/${siteId}/${FEATURES_ENDPOINT}`,
                            {
                                headers: {
                                    Authorization:
                                        'Bearer ' + params.serverToken,
                                },
                            },
                        )
                        .pipe(
                            map(features =>
                                getSiteFeaturesSuccessAction({
                                    siteId,
                                    features,
                                }),
                            ),
                            catchError(error =>
                                of(getSiteFeaturesFailureAction({ error })),
                            ),
                        );
                } else {
                    return this.http
                        .get<
                            Feature[]
                        >(`${SITES_API_URL}/${siteId}/${FEATURES_ENDPOINT}`)
                        .pipe(
                            map(features =>
                                getSiteFeaturesSuccessAction({
                                    siteId,
                                    features,
                                }),
                            ),
                            catchError(error =>
                                of(getSiteFeaturesFailureAction({ error })),
                            ),
                        );
                }
            }),
        ),
    );

    getSiteLicenseRaw$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteLicenseRawRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) => {
                return this.http
                    .get<LicenseRaw>(
                        `${SITES_API_URL}/${siteId}/${LICENSE_ENDPOINT}`,
                    )
                    .pipe(
                        map(license =>
                            getSiteLicenseRawSuccessAction({
                                siteId,
                                licenseRaw: license.licenseRaw,
                            }),
                        ),
                        catchError(error =>
                            of(getSiteLicenseRawFailureAction({ error })),
                        ),
                    );
            }),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store<AppState>,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly httpBackend: HttpBackend,
    ) {
        this.httpWithoutAuth = new HttpClient(this.httpBackend);
    }
}
